/**
 * @fileoverview Final self-assessment drawer (student). There is no submit
 * button: every score is autosaved as it is picked (serialized per id so
 * quick successive clicks never race), and the backend finishes the
 * student's pendency on its own once every item is filled — at that point
 * `onSaved` fires so the tasks list marks the assessment as done (AR-828).
 * Defensive: skeleton, error fallback and id guards (no white screens).
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Center, Text } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { notifications } from 'context'
import { AssessmentDrawerBody, AssessmentDrawerSkeleton, StudentTaskDrawerShell } from '../components'
import { useFinalAssessment } from '../hooks/useFinalAssessment'
import { initialScores } from '../utils'

const ERROR_FALLBACK_HEIGHT = 160
/** Debounce for the "My notes" journal autosave while the student types. */
const JOURNAL_AUTOSAVE_DEBOUNCE_MS = 700

interface StudentFinalAssessmentDrawerProps {
  opened: boolean
  identifierId: string
  onClose: () => void
  onSaved?: () => void
}

export function StudentFinalAssessmentDrawer({
  opened,
  identifierId,
  onClose,
  onSaved,
}: StudentFinalAssessmentDrawerProps) {
  const { t } = useTranslation()
  const { loading, errored, data, autosave } = useFinalAssessment(identifierId, opened)

  const [scores, setScores] = useState<Readonly<Record<string, number>>>({})
  // Mirrors `scores` synchronously: state updates are async, so two quick
  // changes on different items would otherwise merge from a stale snapshot
  // and drop the first one (also skewing the `completed` check below).
  const scoresRef = useRef<Readonly<Record<string, number>>>({})
  // Serializes autosave writes per score id so quick successive clicks on the
  // same item never race / persist out of order on the backend.
  const autosaveChains = useRef<Record<string, Promise<unknown>>>({})

  const [journal, setJournal] = useState('')
  // Mirrors `journal` synchronously so the close-time flush below never
  // persists a stale snapshot of what the student typed.
  const journalRef = useRef('')
  const journalDirty = useRef(false)
  const journalSaveTimeout = useRef<ReturnType<typeof setTimeout>>()

  const allItems = useMemo(
    () => (data ? [...data.skills, ...data.contents] : []),
    [data],
  )

  // Seed the form whenever a (new) task's detail arrives.
  useEffect(() => {
    if (!data) return
    const seeded = initialScores(allItems)
    scoresRef.current = seeded
    setScores(seeded)
    autosaveChains.current = {}
    journalRef.current = data.journal
    journalDirty.current = false
    setJournal(data.journal)
  }, [data, allItems])

  // Persists the journal only: scores ride their own per-id autosave chains,
  // and an empty scores array keeps this write from touching them.
  const persistJournal = () => {
    if (!journalDirty.current) return
    journalDirty.current = false
    void autosave([], journalRef.current)
  }

  const handleJournalChange = (text: string) => {
    journalRef.current = text
    journalDirty.current = true
    setJournal(text)
    if (journalSaveTimeout.current) clearTimeout(journalSaveTimeout.current)
    journalSaveTimeout.current = setTimeout(() => {
      journalSaveTimeout.current = undefined
      persistJournal()
    }, JOURNAL_AUTOSAVE_DEBOUNCE_MS)
  }

  // Flush a pending journal save when the drawer closes, so the last
  // keystrokes are never dropped by the debounce.
  useEffect(() => {
    if (opened) return
    if (journalSaveTimeout.current) {
      clearTimeout(journalSaveTimeout.current)
      journalSaveTimeout.current = undefined
    }
    persistJournal()
  }, [opened])

  const handleScoreChange = (id: string, score: number) => {
    const next = { ...scoresRef.current, [id]: score }
    scoresRef.current = next
    setScores(next)
    const completed = allItems.length > 0 && allItems.every(item => (next[item.id] ?? 0) > 0)
    // Autosave only the changed score, chained per id. Once the last item is
    // filled the assessment counts as concluded (the backend finished the
    // pendency on this same save), so let the parent mark the task as done.
    const previous = autosaveChains.current[id] ?? Promise.resolve()
    autosaveChains.current[id] = previous
      .then(() => autosave([{ id, score }], ''))
      .then(ok => {
        if (!ok) return
        // Same success toast the teacher drawers show on each saved score.
        if (score > 0) {
          notifications.show({ message: t('arc_teacher_assessment_drawer.toast.saved'), color: 'green' })
        }
        if (completed) onSaved?.()
      })
      .catch(() => undefined)
  }

  return (
    <StudentTaskDrawerShell
      opened={opened}
      title={t('student_tasks.assessment_drawer.final_title')}
      onClose={onClose}
    >
      {loading ? (
        <AssessmentDrawerSkeleton />
      ) : errored || !data ? (
        <Center style={{ height: ERROR_FALLBACK_HEIGHT }}>
          <Text color="dimmed" size="sm">{t('student_tasks.assessment_drawer.error_loading')}</Text>
        </Center>
      ) : (
        <AssessmentDrawerBody
          key={data.identifierId}
          projectName={data.projectName}
          skills={data.skills}
          contents={data.contents}
          scores={scores}
          journal={journal}
          onScoreChange={handleScoreChange}
          onJournalChange={handleJournalChange}
        />
      )}
    </StudentTaskDrawerShell>
  )
}
